import React from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import AppSidebar from './AppSidebar'
import { DemoBanner } from '../DemoBanner'
import Header from './Header'
import ImpersonationBanner from './ImpersonationBanner'
import MobileBottomNav from './MobileBottomNav'
import { cn } from '@/lib/utils'
import { useIsMobile } from '@/hooks/useMediaQuery'
import { useImpersonation } from '@/hooks/useImpersonation'
import { useAuth } from '@/hooks/useAuth'
import api from '@/lib/api'
import useAlertNotifications from '@/hooks/useAlertNotifications'
import { useNotifications } from '@/hooks/useNotifications'
import { onSocketEvent, useConnectionHealth } from '@/hooks/useWebSocket'
import { useNavigate } from 'react-router-dom'
import { HelpChatProvider } from '@/components/help/HelpChatContext'
import { HelpChatPanel } from '@/components/help/HelpChatPanel'
import { useRouteDocumentTitle } from '@/hooks/useRouteDocumentTitle'
import { UnlockPromptHost } from '@/components/unlock/UnlockPrompt'
import CalendarPeekRail, { PEEK_RAIL_WIDTH_PX } from '@/components/bookings/CalendarPeekRail'

const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000 // 5 minutes

export default function DashboardLayout() {
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const location = useLocation()
  const { isImpersonating } = useImpersonation()
  const { agent } = useAuth()
  useAlertNotifications()
  useRouteDocumentTitle()
  const connectionHealth = useConnectionHealth()
  const { getUnreadBySection, markSectionAsRead } = useNotifications()
  const notificationBadges = getUnreadBySection()

  // Human request alert state
  const [humanRequestAlert, setHumanRequestAlert] = React.useState<{
    visitorId: string
    sessionId: string
    timestamp: string
  } | null>(null)

  React.useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail
      setHumanRequestAlert({
        visitorId: detail?.visitorId || 'unknown',
        sessionId: detail?.sessionId || '',
        timestamp: detail?.timestamp || new Date().toISOString(),
      })
      // Auto-dismiss after 30 seconds
      setTimeout(() => setHumanRequestAlert(null), 30_000)
    }
    window.addEventListener('human-requested', handler)
    return () => window.removeEventListener('human-requested', handler)
  }, [])

  // Debounced connection banner — wait 10s before showing, suppress during initial connection
  const [showConnectionBanner, setShowConnectionBanner] = React.useState(false)
  const mountTimeRef = React.useRef(Date.now())
  React.useEffect(() => {
    if (connectionHealth === 'healthy') {
      setShowConnectionBanner(false)
      return
    }
    // Suppress during initial 10-second connection window
    if (Date.now() - mountTimeRef.current < 10000) return
    const timer = setTimeout(() => setShowConnectionBanner(true), 10000)
    return () => clearTimeout(timer)
  }, [connectionHealth])

  // Live badge counters for events that don't create notification records
  const [liveBadges, setLiveBadges] = React.useState<Record<string, number>>({})

  // Listen for team messages and visitor messages via WebSocket
  // Note: visitor:connected removed — only notify when visitor actually sends a message
  React.useEffect(() => {
    const cleanups = [
      onSocketEvent('team:message', (data: any) => {
        if (data?.senderId === agent?.id) return // Skip own messages
        if (location.pathname !== '/messages') {
          setLiveBadges(prev => ({ ...prev, '/messages': (prev['/messages'] || 0) + 1 }))
        }
      }),
      onSocketEvent('visitor:message', (data: any) => {
        // Only badge for actual visitor messages, not bot/agent/system messages
        if (data?.role === 'agent' || data?.role === 'bot' || data?.role === 'system') return
        if (location.pathname !== '/chats') {
          setLiveBadges(prev => ({ ...prev, '/chats': (prev['/chats'] || 0) + 1 }))
        }
      }),
      onSocketEvent('sms:message', (data: any) => {
        if (data?.event === 'sms_received' && location.pathname !== '/sms') {
          setLiveBadges(prev => ({ ...prev, '/sms': (prev['/sms'] || 0) + 1 }))
        }
      }),
      onSocketEvent('lead:new', (_data: any) => {
        // Matches the pattern of other live badge events (visitor:message,
        // sms:message) which broadcast to all agents in the room. Suppress
        // if already on the leads page.
        if (location.pathname.startsWith('/leads')) return
        setLiveBadges(prev => ({ ...prev, '/leads': (prev['/leads'] || 0) + 1 }))
      }),
    ]
    return () => cleanups.forEach(fn => fn())
  }, [location.pathname])

  // Merge notification-based badges with live WebSocket badges
  const sidebarBadges = React.useMemo(() => {
    const merged = { ...notificationBadges }
    for (const [key, count] of Object.entries(liveBadges)) {
      merged[key] = (merged[key] || 0) + count
    }
    return merged
  }, [notificationBadges, liveBadges])

  // Clear badges when navigating to a section
  React.useEffect(() => {
    markSectionAsRead(location.pathname)
    // Also clear live badges for this section
    setLiveBadges(prev => {
      if (prev[location.pathname]) {
        const next = { ...prev }
        delete next[location.pathname]
        return next
      }
      return prev
    })
  }, [location.pathname, markSectionAsRead])

  // Keep last_active fresh while the user is using the dashboard
  React.useEffect(() => {
    const sendHeartbeat = () => {
      api.post('/api/auth/heartbeat').catch(() => { /* non-critical */ })
    }
    sendHeartbeat()
    const interval = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [])

  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed')
    return saved === 'true'
  })

  // Calendar peek rail — Outlook-style right pane. Default closed so existing
  // users aren't surprised by a narrower dashboard. Hidden on mobile entirely.
  const [peekRailOpen, setPeekRailOpen] = React.useState(() => {
    return localStorage.getItem('calendar-peek-rail-open') === 'true'
  })

  const togglePeekRail = () => {
    setPeekRailOpen(prev => {
      const next = !prev
      localStorage.setItem('calendar-peek-rail-open', String(next))
      return next
    })
  }

  // On mobile, sidebar is hidden by default and shown as overlay
  const [mobileSidebarOpen, setMobileSidebarOpen] = React.useState(false)

  const toggleSidebar = () => {
    if (isMobile) {
      setMobileSidebarOpen(prev => !prev)
    } else {
      setSidebarCollapsed(prev => {
        const newValue = !prev
        localStorage.setItem('sidebar-collapsed', String(newValue))
        return newValue
      })
    }
  }

  // Close mobile sidebar when switching to desktop
  React.useEffect(() => {
    if (!isMobile) {
      setMobileSidebarOpen(false)
    }
  }, [isMobile])

  return (
    <HelpChatProvider>
    <div className="min-h-screen bg-background">
      {/* Global trial unlock prompt — fires at natural moments (lead count hit, missed call, etc.) */}
      <UnlockPromptHost />

      {/* Impersonation Banner */}
      <ImpersonationBanner />

      {/* Connection status banner */}
      {showConnectionBanner && connectionHealth !== 'healthy' && (
        <div className={cn(
          "fixed top-0 left-0 right-0 z-50 text-center text-sm py-1.5 transition-colors",
          connectionHealth === 'degraded'
            ? "bg-yellow-500/90 text-yellow-950"
            : "bg-red-500/90 text-white"
        )}>
          {connectionHealth === 'degraded'
            ? 'Connection is slow. Some updates may be delayed.'
            : 'Connection lost. Reconnecting...'}
        </div>
      )}

      {/* Human request urgent alert banner */}
      {humanRequestAlert && (
        <div className="fixed top-0 left-0 right-0 z-[60] animate-pulse">
          <div className="bg-orange-500 text-white text-center py-3 px-4 shadow-lg flex items-center justify-center gap-3">
            <span className="text-lg">&#9888;</span>
            <span className="font-semibold">A visitor is requesting to speak with a real person!</span>
            <button
              onClick={() => {
                navigate('/chats')
                setHumanRequestAlert(null)
              }}
              className="ml-2 bg-white text-orange-600 font-bold px-4 py-1 rounded-md text-sm hover:bg-orange-50 transition-colors"
            >
              Go to Chat
            </button>
            <button
              onClick={() => setHumanRequestAlert(null)}
              className="ml-1 text-white/80 hover:text-white text-lg leading-none"
              aria-label="Dismiss"
            >
              &times;
            </button>
          </div>
        </div>
      )}

      {/* Mobile overlay backdrop */}
      {isMobile && mobileSidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 backdrop-blur-sm"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <AppSidebar
        collapsed={sidebarCollapsed}
        onToggle={toggleSidebar}
        isMobile={isMobile}
        mobileOpen={mobileSidebarOpen}
        onMobileClose={() => setMobileSidebarOpen(false)}
        badges={sidebarBadges}
      />

      {/* Header */}
      <Header
        sidebarCollapsed={sidebarCollapsed}
        isMobile={isMobile}
        onMenuClick={toggleSidebar}
        peekRailOpen={!isMobile && peekRailOpen}
      />

      {/* Main content */}
      <main
        className={cn(
          'transition-[margin] duration-200 min-h-screen brand-pattern',
          isImpersonating ? 'pt-[calc(4rem+2.5rem)] pb-20' : 'pt-16',
          // Bottom nav on mobile takes ~64px + safe area; clear it.
          isMobile ? 'ml-0 pb-[calc(4rem+env(safe-area-inset-bottom))]' : (sidebarCollapsed ? 'ml-16' : 'ml-64')
        )}
        style={{
          marginRight: !isMobile && peekRailOpen ? `${PEEK_RAIL_WIDTH_PX}px` : undefined,
          background: 'radial-gradient(ellipse at top left, hsl(var(--primary) / 0.03) 0%, transparent 50%), radial-gradient(ellipse at bottom right, hsl(var(--primary) / 0.02) 0%, transparent 50%), hsl(var(--background))'
        }}
      >
        <DemoBanner />
        <div key={location.pathname} className="p-2 sm:p-3 md:p-4 lg:p-6 animate-fade-in-up min-w-0 max-w-full overflow-x-hidden">
          <Outlet />
        </div>
      </main>

      {/* Calendar peek rail — desktop only. Hidden on mobile (Phase 3 will add a
          mobile-equivalent "Today" card on the dashboard itself). */}
      {!isMobile && (
        <CalendarPeekRail
          open={peekRailOpen}
          onToggle={togglePeekRail}
          topOffsetClass={isImpersonating ? 'top-10 h-[calc(100vh-2.5rem)]' : 'top-0 h-screen'}
        />
      )}

      {/* Mobile bottom navigation — only renders on mobile via internal md:hidden.
          Suppressed inside Platform Admin where the org-side tabs would be
          confusing; admins navigate /admin/* via the side sheet. */}
      {isMobile && !location.pathname.startsWith('/admin') && <MobileBottomNav badges={sidebarBadges} />}

      {/* Ask LegalLady help panel */}
      <HelpChatPanel />
    </div>
    </HelpChatProvider>
  )
}